import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { KnowledgeLibraryFile, KnowledgeLibraryService } from '../../services/knowledge-library.service';
import { TranslateService } from '@ngx-translate/core';
import { FileUploadValidationService } from '../../services/file-upload-validation.service';
import { ToastrService } from 'ngx-toastr';

import { NavTitlePipe } from '../../pipes/nav-title.pipe';

@Component({
  selector: 'app-knowledge-library',
  standalone: true,
  imports: [
    NavTitlePipe,
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    ConfirmDialogModule
  ],
  templateUrl: './knowledge-library.component.html',
  styleUrl: './knowledge-library.component.scss',
  providers: [ConfirmationService]
})
export class KnowledgeLibraryComponent implements OnInit, OnDestroy {
  files: KnowledgeLibraryFile[] = [];
  loading = false;

  canUpload = false;
  uploadName = '';
  selectedFile: File | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private knowledgeLibraryService: KnowledgeLibraryService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private fileValidation: FileUploadValidationService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.authService.authBufferSub$.pipe(takeUntil(this.destroy$)).subscribe((u) => {
      this.canUpload = !!u?.strategy_admin;
    });
    this.refresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canSubmitUpload(): boolean {
    return this.canUpload && !!this.selectedFile && !!this.uploadName?.trim();
  }

  refresh() {
    this.loading = true;
    this.knowledgeLibraryService.listFiles().subscribe({
      next: (res) => {
        this.files = (res?.data?.files ?? res?.files ?? []) as KnowledgeLibraryFile[];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  onFileSelected(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    if (!file) {
      this.selectedFile = null;
      return;
    }
    const result = this.fileValidation.validateFile(file, {
      maxBytes: FileUploadValidationService.KNOWLEDGE_LIBRARY_MAX_BYTES
    });
    if (!result.valid) {
      this.selectedFile = null;
      input.value = '';
      this.toastr.error(this.translate.instant('upload_validation_failed') || 'File type or size is not allowed.');
      return;
    }
    this.selectedFile = file;
  }

  upload() {
    if (!this.canSubmitUpload || !this.selectedFile) return;

    const name = this.uploadName.trim();
    this.loading = true;
    this.knowledgeLibraryService.uploadFile(name, this.selectedFile).subscribe({
      next: () => {
        this.uploadName = '';
        this.selectedFile = null;
        this.refresh();
      },
      error: () => {
        this.loading = false;
        this.toastr.error(this.translate.instant('upload_failed') || 'Upload failed. Please try again.');
      }
    });
  }

  fileName(row: KnowledgeLibraryFile): string {
    const lang = (this.translate.currentLang || this.translate.defaultLang || 'ar').toLowerCase();
    if (lang.startsWith('en')) {
      return (row.file_name || row.display_name || '').trim();
    }
    return (row.display_name || row.file_name || '').trim();
  }

  download(row: KnowledgeLibraryFile) {
    this.knowledgeLibraryService.downloadFile(row.id).subscribe((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.file_name || `${row.display_name || 'file'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  remove(row: KnowledgeLibraryFile) {
    if (!this.canUpload) return;
    const header = this.translate.instant('confirm_delete_title');
    const message = this.translate.instant('confirm_delete_file_message', { name: row.display_name });
    const acceptLabel = this.translate.instant('yes');
    const rejectLabel = this.translate.instant('no');

    this.confirmationService.confirm({
      header,
      message,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel,
      rejectLabel,
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.loading = true;
        this.knowledgeLibraryService.softDelete(row.id).subscribe({
          next: () => this.refresh(),
          error: () => { this.loading = false; }
        });
      }
    });
  }

  formatSize(bytes: number) {
    if (!bytes && bytes !== 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(1)} GB`;
  }
}

